'use client';

import { useCallback, useRef, useState } from 'react';
import type { FC } from 'react';
import { Input } from '@/components/ui/input';

interface MaskedDateInputProps {
  /** Input element id. */
  id?: string;
  /** Form field name — the raw MM/DD/YYYY string is submitted. */
  name?: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Placeholder text. Defaults to "MM/DD/YYYY". */
  placeholder?: string;
  /** Additional class names for the input. */
  className?: string;
}

/**
 * Masked date input that auto-inserts `/` separators as the user types.
 * Only allows numeric input and constrains month (01–12) and day (01–31).
 */
export const MaskedDateInput: FC<MaskedDateInputProps> = ({
  id,
  name,
  required,
  placeholder = 'MM/DD/YYYY',
  className,
}) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      /* Allow clearing the field. */
      if (raw === '') {
        setValue('');
        return;
      }

      /* Detect backspace: new value is shorter than previous. */
      if (raw.length < value.length) {
        setValue(raw);
        return;
      }

      /* Strip everything except digits from the full value. */
      const digits = raw.replace(/\D/g, '');
      setValue(formatDateDigits(digits));
    },
    [value],
  );

  return (
    <Input
      ref={inputRef}
      id={id}
      name={name}
      required={required}
      placeholder={placeholder}
      className={className}
      value={value}
      onChange={handleChange}
      maxLength={10}
      inputMode="numeric"
    />
  );
};

/**
 * Takes a string of digits (up to 8) and formats them as MM/DD/YYYY,
 * constraining month to 01–12 and day to 01–31.
 */
function formatDateDigits(digits: string): string {
  let result = '';
  const d = digits.slice(0, 8);

  for (let i = 0; i < d.length; i++) {
    const char = d[i];

    if (i === 0) {
      /* Month first digit: 0 or 1 only. If they type 2–9, auto-prefix with 0. */
      if (char > '1') {
        result += '0' + char + '/';
        continue;
      }
      result += char;
    } else if (i === 1) {
      /* Month second digit: 1X caps at 12, 00 is invalid. */
      if (d[0] === '1' && char > '2') continue;
      if (d[0] === '0' && char === '0') continue;
      result += char + '/';
    } else if (i === 2) {
      /* Day first digit: 0–3 only. If they type 4–9, auto-prefix with 0. */
      if (char > '3') {
        result += '0' + char + '/';
        continue;
      }
      result += char;
    } else if (i === 3) {
      /* Day second digit: 3X caps at 31, 00 is invalid. */
      if (d[2] === '3' && char > '1') continue;
      if (d[2] === '0' && char === '0') continue;
      result += char + '/';
    } else {
      /* Year digits (4–7). */
      result += char;
    }
  }

  return result;
}
